import { useState } from 'react';
import { useSEO } from '@/hooks/useSEO';
import { useTranslation } from 'react-i18next';

const ContactPage = () => {
  const { t } = useTranslation();

  const contactInfo = [
    { icon: 'ri-map-pin-line', title: t('contact_address'), value: t('contact_address_val') },
    { icon: 'ri-mail-line', title: t('contact_email_label'), value: t('contact_email_val') },
    { icon: 'ri-time-line', title: t('contact_hours'), value: t('contact_hours_val') },
    { icon: 'ri-information-line', title: t('contact_reach'), value: t('contact_reach_val') },
  ];

  const socialLinks = [
    { icon: 'ri-instagram-line', name: 'Instagram', url: 'https://www.instagram.com/spiritualshop.nl', handle: '@spiritualshop.nl' },
    { icon: 'ri-tiktok-line', name: 'TikTok', url: 'https://www.tiktok.com/@spiritualshop', handle: '@spiritualshop' },
    { icon: 'ri-youtube-line', name: 'YouTube', url: 'https://www.youtube.com/@SpiritualShopNL', handle: 'SpiritualShop NL' },
    { icon: 'ri-facebook-circle-line', name: 'Facebook', url: 'https://www.facebook.com/SpiritualShopNL', handle: 'SpiritualShop NL' },
    { icon: 'ri-pinterest-line', name: 'Pinterest', url: 'https://www.pinterest.com/spiritualshopnl', handle: '@spiritualshopnl' },
    { icon: 'ri-whatsapp-line', name: 'WhatsApp', url: 'https://wa.me/31698765432', handle: '+31 6 98765432' },
  ];

  useSEO({
    title: 'Contact | ZpiritualZ Den Haag – Spirituele Webshop Kobaltstraat 21',
    description: 'Neem contact op met ZpiritualZ in Den Haag (Kobaltstraat 21). Vragen over spirituele beschermingsproducten, bestellingen of consultaties? Mail info@zpiritualz.com.',
    canonical: 'https://www.zpiritualz.com/contact',
    keywords: 'ZpiritualZ contact, spirituele winkel Den Haag, Kobaltstraat 21 Den Haag, spirituele webshop contact, info@zpiritualz.com',
    ogImage: 'https://public.readdy.ai/ai/img_res/1dfccabf79d0b7ca80de806288e767b2.jpg',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: 'ZpiritualZ',
      url: 'https://www.zpiritualz.com',
      email: 'info@zpiritualz.com',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Kobaltstraat 21',
        addressLocality: 'Den Haag',
        postalCode: '2544 ET',
        addressCountry: 'NL',
      },
      geo: { '@type': 'GeoCoordinates', latitude: '52.0489', longitude: '4.2867' },
      openingHoursSpecification: [{ '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'], opens: '10:00', closes: '17:00' }],
      sameAs: ['https://www.instagram.com/spiritualshop.nl', 'https://www.facebook.com/SpiritualShopNL'],
    },
  });

  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('sending');
    const form = e.target as HTMLFormElement;
    const data = new FormData(form);
    const params = new URLSearchParams();
    data.forEach((value, key) => params.append(key, value.toString()));
    try {
      await fetch('https://readdy.ai/api/form/d6thm6cj8d20juc904a0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      setFormStatus('sent');
      form.reset();
    } catch {
      setFormStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-[#08080f] pt-20">
      {/* Hero */}
      <div className="py-16 text-center border-b border-white/5">
        <p className="text-[#d4a017] text-xs font-semibold uppercase tracking-widest mb-3">{t('contact_badge')}</p>
        <h1 className="text-5xl font-bold text-white mb-4">
          <span className="text-transparent bg-gradient-to-r from-teal-400 to-[#d4a017] bg-clip-text">{t('contact_title')}</span>
        </h1>
        <p className="text-white/60 text-lg max-w-xl mx-auto">{t('contact_sub')}</p>
      </div>

      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left: Info + Social */}
          <div>
            <h2 className="text-2xl font-bold text-white mb-8">{t('contact_info_title')}</h2>
            <div className="space-y-5 mb-10">
              {contactInfo.map(item => (
                <div key={item.title} className="flex items-start gap-4 p-4 bg-white/3 border border-white/8 rounded-xl">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-teal-500/10 shrink-0">
                    <i className={`${item.icon} text-teal-400`} />
                  </div>
                  <div>
                    <p className="text-white/40 text-xs mb-1">{item.title}</p>
                    {item.title === t('contact_email_label') ? (
                      <a href={`mailto:${item.value}`} className="text-teal-400 text-sm hover:text-teal-300 transition-colors">{item.value}</a>
                    ) : (
                      <p className="text-white text-sm">{item.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Map */}
            <div className="rounded-2xl overflow-hidden h-48 bg-white/5 border border-white/8 mb-10">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2454.3!2d4.2867!3d52.0489!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47c5b72f5b8f3b9b%3A0x0!2sKobaltstraat%2021%2C%202544%20ET%20Den%20Haag!5e0!3m2!1snl!2snl!4v1"
                width="100%" height="100%" style={{ border: 0 }} loading="lazy"
                title={t('contact_map_title')}
              />
            </div>

            {/* Social Media */}
            <h3 className="text-white font-semibold text-lg mb-5">{t('contact_social_title')}</h3>
            <div className="grid grid-cols-2 gap-3">
              {socialLinks.map(s => (
                <a key={s.name} href={s.url} target="_blank" rel="nofollow noreferrer"
                  className="flex items-center gap-3 p-3 bg-white/3 border border-white/8 rounded-xl hover:border-teal-500/30 transition-all cursor-pointer group">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 group-hover:bg-teal-500/10 transition-colors">
                    <i className={`${s.icon} text-white/60 group-hover:text-teal-400 transition-colors`} />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium leading-none">{s.name}</p>
                    <p className="text-white/30 text-xs mt-0.5">{s.handle}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* Right: Contact Form */}
          <div>
            <div className="bg-white/3 border border-white/10 rounded-3xl p-8">
              <h2 className="text-2xl font-bold text-white mb-6">{t('contact_form_title')}</h2>

              {formStatus === 'sent' ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-teal-500/20 mx-auto mb-4">
                    <i className="ri-check-line text-3xl text-teal-400" />
                  </div>
                  <h3 className="text-white font-bold text-xl mb-2">{t('contact_success_title')}</h3>
                  <p className="text-white/50 text-sm">{t('contact_success_msg')}</p>
                  <button onClick={() => setFormStatus('idle')}
                    className="mt-6 px-6 py-3 border border-teal-500/30 text-teal-400 rounded-xl text-sm hover:bg-teal-500/10 transition-colors cursor-pointer whitespace-nowrap">
                    {t('cta_newMessage')}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form id="contact-main-form" className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white/60 text-xs mb-2">{t('contact_form_first')}</label>
                      <input name="first_name" type="text" required className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-teal-500/50 transition-colors" placeholder={t('contact_form_ph_first')} />
                    </div>
                    <div>
                      <label className="block text-white/60 text-xs mb-2">{t('contact_form_last')}</label>
                      <input name="last_name" type="text" required className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-teal-500/50 transition-colors" placeholder={t('contact_form_ph_last')} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-white/60 text-xs mb-2">{t('contact_form_email')}</label>
                    <input name="email" type="email" required className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-teal-500/50 transition-colors" placeholder={t('contact_form_ph_email')} />
                  </div>
                  <div>
                    <label className="block text-white/60 text-xs mb-2">{t('contact_form_subject')}</label>
                    <select name="subject" required className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white focus:outline-none focus:border-teal-500/50 transition-colors appearance-none cursor-pointer">
                      <option value="" className="bg-[#0d0d1a]">{t('contact_form_subject_ph')}</option>
                      <option value="product" className="bg-[#0d0d1a]">{t('contact_form_subject_product')}</option>
                      <option value="order" className="bg-[#0d0d1a]">{t('contact_form_subject_order')}</option>
                      <option value="consultation" className="bg-[#0d0d1a]">{t('contact_form_subject_consult')}</option>
                      <option value="samenwerking" className="bg-[#0d0d1a]">{t('contact_form_subject_collab')}</option>
                      <option value="overig" className="bg-[#0d0d1a]">{t('contact_form_subject_other')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-white/60 text-xs mb-2">{t('contact_form_msg')}</label>
                    <textarea name="message" required rows={5} maxLength={500}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-teal-500/50 transition-colors resize-none"
                      placeholder={t('contact_form_ph_msg')} />
                    <p className="text-white/20 text-xs mt-1">{t('contact_form_max')}</p>
                  </div>
                  {formStatus === 'error' && (
                    <p className="text-rose-400 text-sm text-center">{t('contact_error')}</p>
                  )}
                  <button type="submit" disabled={formStatus === 'sending'}
                    className="w-full py-4 bg-gradient-to-r from-teal-500 to-[#d4a017] text-[#08080f] font-bold rounded-xl whitespace-nowrap hover:opacity-90 transition-opacity cursor-pointer disabled:opacity-50">
                    {formStatus === 'sending' ? t('cta_sending') : t('cta_sendMessage')}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
